"use client";

import React from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { Heart, Eye, MessageSquare } from "lucide-react";
import DetailMetaHeader from "@/components/common/DetailMetaHeader";
import { AuthorInfo } from "@/components/common/AuthorChip";
import MiniTalentCard from "@/components/common/MiniTalentCard";
import LikeShareReport from "@/components/common/LikeShareReport";
import FriendButton from "@/components/profile/FriendButton";
import FollowButton from "@/components/profile/FollowButton";
import ProfileCTAs from "@/components/profile/ProfileCTAs";
import { apiFetch } from "@/lib/api";
import { toggleLike as engageToggle, getLikeState } from "@/lib/engagement";
import { useEngagement } from "@/context/EngagementContext";
import { scrubContactInfo } from "@/lib/scrubContact";

type DetailType = "video" | "gallery" | "post" | "profile";

export default function DetailToolbar({
  type,
  id,
  title,
  postedAt,
  author,
  stats,
  liked: likedInitial,
  likeable = true,
  onToggleLike,
  share = true,
  report = true,
  sticky = false,
  juicyTitle = true,
  reportUserId,
  friendPersonId,
  ctaPersonId,
  followPersonId,
  variant = 'default',
}: {
  type: DetailType;
  id?: number | string | null;
  title: React.ReactNode;
  postedAt?: string | Date | null;
  author?: AuthorInfo | null;
  stats?: { views?: number | null; likes?: number | null; comments?: number | null };
  liked?: boolean;
  likeable?: boolean;
  onToggleLike?: () => Promise<{ liked: boolean; likeCount?: number } | void>;
  share?: boolean;
  report?: boolean;
  sticky?: boolean;
  /** Controls gradient/brand styling on title; defaults to true for videos/galleries/talent */
  juicyTitle?: boolean;
  /** For profile type reports, pass the target user GUID */
  reportUserId?: string | null;
  /** When provided, shows Add Friend control in the action bar */
  friendPersonId?: string | number | null;
  /** When provided, shows Chat/Email CTAs inline in the action bar (profile pages) */
  ctaPersonId?: string | number | null;
  /** When provided, shows the Follow toggle in the action bar (profile pages, non-self only) */
  followPersonId?: number | null;
  /** "banner" renders just the action buttons inline (no panel chrome) for use in DetailsBanner */
  variant?: 'default' | 'banner';
}) {
  const { data: session, status } = useSession();
  const eng = useEngagement();
  const router = useRouter();
  const pathname = usePathname();
  const isAuthed = status === 'authenticated';
  const [liked, setLiked] = React.useState<boolean>(!!likedInitial);
  const [likeCount, setLikeCount] = React.useState<number>(typeof stats?.likes === 'number' ? stats!.likes! : 0);

  // Scrub title if it's a string
  const displayTitle = React.useMemo(() => {
    if (typeof title === 'string') {
      return scrubContactInfo(title);
    }
    return title;
  }, [title]);

  React.useEffect(() => { if (typeof stats?.likes === 'number') setLikeCount(stats!.likes!); }, [stats?.likes]);
  React.useEffect(() => { setLiked(!!likedInitial); }, [likedInitial]);
  React.useEffect(() => {
    if (!id) return;
    if (type === 'video' || type === 'gallery' || type === 'post' || type === 'profile') {
      const st = eng.getState(type as any, id);
      if (st) {
        setLiked(st.liked);
        if (typeof st.likeCount === 'number') setLikeCount(st.likeCount);
      }
    }
  }, [eng, type, id]);

  // Unified like state fetch to ensure persistence across reloads
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      const s = await getLikeState(type as any, id!, (status === 'authenticated' ? (session as any) : undefined));
      if (cancelled || !s) return;
      if (typeof s.liked === 'boolean') setLiked(s.liked);
      if (typeof s.likeCount === 'number') setLikeCount(s.likeCount);
      try { eng.setState(type as any, id!, s); } catch {}
    })();
    return () => { cancelled = true; };
  }, [type, id, status]);

  const dateStr = postedAt ? new Date(postedAt).toLocaleDateString() : undefined;

  async function toggleLikeInternal() {
    if (!likeable) return;
    if (!isAuthed) {
      // Same guest gate as every other action in this row: register with
      // returnTo, not a dead-end "Sign in required" alert.
      router.push(`/register?returnTo=${encodeURIComponent(pathname || '/')}`);
      return;
    }
    const prev = liked;
    setLiked(!prev);
    setLikeCount((c) => Math.max(0, c + (prev ? -1 : 1)));
    try {
      if (onToggleLike) {
        const res = await onToggleLike();
        if (res && typeof res === 'object') {
          if (typeof res.liked === 'boolean') setLiked(res.liked);
          if (typeof res.likeCount === 'number') setLikeCount(res.likeCount);
        }
        return;
      }
      if (!id) throw new Error('Missing id');
      // Profile: treat as proper like (independent from friendship)
      if (type === 'profile') {
        try {
          const res = await eng.toggleLike('profile', id!, session);
          if (res) {
            if (typeof res.liked === 'boolean') setLiked(res.liked);
            if (typeof res.likeCount === 'number') setLikeCount(res.likeCount);
          }
        } catch (e) {
          // Revert + optionally inform the user; endpoint may not exist yet
          setLiked(prev);
          setLikeCount((c) => Math.max(0, c + (prev ? 1 : -1)));
        }
        return;
      }
      if (type === 'video' || type === 'gallery' || type === 'post') {
        const res = await eng.toggleLike(type as any, id, session);
        if (res) {
          if (typeof res.liked === 'boolean') setLiked(res.liked);
          if (typeof res.likeCount === 'number') setLikeCount(res.likeCount);
        }
      }
    } catch (e) {
      setLiked(prev);
      setLikeCount((c) => Math.max(0, c + (prev ? 1 : -1)));
    }
  }

  // Banner: ONE primary (Chat) and scrim-styled secondaries, per the design
  // system's "saturated cherry reserved for the action that matters". The old
  // row was four identical primaries. h-11 on mobile = 44px tap targets.
  const isBanner = variant === 'banner';
  const bannerBtnCls = 'h-11 md:h-9';
  const actionButtons = isBanner ? (
    <div className="flex items-center gap-2 flex-wrap">
      {ctaPersonId != null && (
        <>
          <ProfileCTAs personId={ctaPersonId} variant="brand" size="sm" layout="inline" showEmail={false} buttonClassName={bannerBtnCls} />
          <ProfileCTAs personId={ctaPersonId} variant="brand" size="sm" layout="inline" showChat={false} appearance="scrim" buttonClassName={bannerBtnCls} />
        </>
      )}
      {followPersonId != null && followPersonId > 0 && (
        <FollowButton personId={followPersonId} showCount={false} appearance="scrim" className={bannerBtnCls} />
      )}
      {friendPersonId != null && (
        <FriendButton personId={friendPersonId} size="sm" appearance="scrim" className={bannerBtnCls} />
      )}
      <LikeShareReport
        type={type}
        id={id}
        liked={liked}
        likeCount={likeCount}
        likeable={likeable}
        onToggleLike={toggleLikeInternal}
        share={share}
        report={report}
        reportUserId={reportUserId}
        showCounts={false}
        compact
      />
    </div>
  ) : (
    <div className="flex items-center gap-2 flex-wrap">
      {followPersonId != null && followPersonId > 0 && (
        <FollowButton personId={followPersonId} showCount={false} />
      )}
      {ctaPersonId != null && (
        <ProfileCTAs personId={ctaPersonId} variant="brand" size="sm" layout="inline" className="" />
      )}
      {friendPersonId != null && (
        <FriendButton personId={friendPersonId} size="sm" />
      )}
      <LikeShareReport
        type={type}
        id={id}
        liked={liked}
        likeCount={likeCount}
        likeable={likeable}
        onToggleLike={toggleLikeInternal}
        share={share}
        report={report}
        reportUserId={reportUserId}
        showCounts={false}
        compact
      />
    </div>
  );

  if (isBanner) {
    return <div className="flex items-center gap-2 flex-wrap">{actionButtons}</div>;
  }

  const chipStats: { label: string; value: number; icon?: React.ReactNode }[] = [];
  if (typeof stats?.views === 'number') chipStats.push({ label: 'views', value: stats!.views!, icon: (<Eye className="h-4 w-4" aria-hidden />) });
  if (typeof stats?.likes === 'number') chipStats.push({ label: 'likes', value: stats!.likes!, icon: (<Heart className="h-4 w-4" aria-hidden />) });
  if (typeof stats?.comments === 'number') chipStats.push({ label: 'comments', value: stats!.comments!, icon: (<MessageSquare className="h-4 w-4" aria-hidden />) });

  const subtitle = (
    <div className="flex items-center gap-2">
      {author ? (
        <MiniTalentCard
          id={author.id}
          name={author.name}
          avatarUrl={author.avatarUrl || undefined}
          profileUrl={author.profileUrl}
        />
      ) : null}
      {author && postedAt ? <span>•</span> : null}
      {dateStr ? <span>{dateStr}</span> : null}
    </div>
  );

  // Do not show MiniTalentCard in the header panels; keep only a posted date if provided.
  const subtitleNode = dateStr ? (
    <div className="flex items-center gap-2"><span>{dateStr}</span></div>
  ) : null;

  // Two-panel layout:
  // 1) Top card: title + actions (like/share/report) on the same line
  // 2) Secondary card: author/subtitle + stats on its own line to clarify context
  const wrapperClasses = sticky ? "sticky top-header z-[40]" : undefined;
  const isProfile = type === 'profile';
  const [showProfileIdentity, setShowProfileIdentity] = React.useState(false);
  React.useEffect(() => {
    if (!isProfile) return;
    const el = typeof document !== 'undefined' ? document.getElementById('banner-avatar-sentinel') : null;
    if (!el) { setShowProfileIdentity(true); return; }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // When the hero avatar comes into view, switch identity flag
        setShowProfileIdentity(entry.isIntersecting);
      }
    }, { rootMargin: '0px', threshold: 0 });
    try { io.observe(el); } catch {}
    return () => { try { io.disconnect(); } catch {} };
  }, [isProfile]);
  return (
    <>
      <div className={wrapperClasses} id={isProfile ? 'profile-controls' : undefined}>
        <div className="rounded-xl border border-border bg-black/5 dark:bg-white/10 p-3 md:p-4 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-3">
            {/* For profile pages, remove author chip from this bar. Keep it clean for actions. */}
            {!isProfile && (
              <h1 className={("text-2xl md:text-3xl font-semibold leading-tight " + (juicyTitle ? "font-js js-yl2rd" : "")).trim()}>
                {displayTitle}
              </h1>
            )}
            <div className="md:ml-auto flex items-center gap-2">
              {actionButtons}
            </div>
          </div>
        </div>
      </div>

      {(subtitleNode || (chipStats && chipStats.length > 0)) && (
        <div className="mt-3 rounded-xl border border-border bg-black/5 dark:bg-white/10 p-3 md:p-4 shadow-sm">
          {subtitleNode ? (
            <div className="mb-2 last:mb-0">
              {subtitleNode}
            </div>
          ) : null}
          {chipStats && chipStats.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {chipStats.map((s, i) => (
                <span key={(s as any).key || `${(s as any).label}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-1 text-xs">
                  {(s as any).icon ? <span className="opacity-80">{(s as any).icon}</span> : null}
                  <span className="font-medium leading-none">
                    {typeof (s as any).value === 'number' ? ((s as any).value as number).toLocaleString() : ((s as any).value ?? '-')}
                  </span>
                  <span className="opacity-70 leading-none">{(s as any).label}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
